// "Konfiguraation tila" — four config-completeness status cards that ARE the Konfiguraatio
// tab bar (always visible, so validity reads at a glance without entering a tab). Each card is
// a Tier-2 interactive entity tile: moduleCard + CardGlowOverlay (the living background). No live
// values — status is derived from configuration only (see useBuildingConfigStatus).
//
// Above the cards sits the canonical rollup (coverage %, configured/total, stage) from
// `GET /buildings/{id}/completeness` — the same signal the portfolio Konfiguraatio view reads.
import { moduleCard, cardToneText } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { KpiStatusTileContent } from "@/components/ui/kpi-status-tile-content";
import { cn } from "@/lib/utils";
import type { BuildingCompleteness } from "@/lib/api/agent";
import {
  completenessStage,
  coveragePercent,
  stageLabel,
} from "@/lib/config-completeness";
import { TAB_ICON } from "@/lib/config-status-dimensions";
import {
  useBuildingConfigStatus,
  type ConfigDimension,
  type ConfigTabKey,
} from "@/lib/useBuildingConfigStatus";

export function ConfigStatusTabs({
  buildingId,
  activeTab,
  onSelect,
  refreshKey,
}: {
  buildingId: string;
  activeTab: ConfigTabKey;
  onSelect: (tab: ConfigTabKey) => void;
  refreshKey?: number;
}) {
  const { loading, dimensions, completeness } = useBuildingConfigStatus(
    buildingId,
    refreshKey,
  );

  return (
    <div className="space-y-3">
      {completeness && <CompletenessRollup completeness={completeness} />}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {loading
          ? [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(moduleCard, "h-28 animate-pulse p-4")}
              />
            ))
          : dimensions.map((dim) => (
              <StatusTab
                key={dim.key}
                dim={dim}
                active={dim.key === activeTab}
                onClick={() => onSelect(dim.key)}
              />
            ))}
      </div>
    </div>
  );
}

/** The building rollup — the same coverage %, counts and stage the portfolio view reports. */
function CompletenessRollup({
  completeness,
}: {
  completeness: BuildingCompleteness;
}) {
  const coverage = Math.round(coveragePercent(completeness));
  const stage = completenessStage(completeness);
  const tone =
    stage === "ready" ? "ok" : stage === "partial" ? "warn" : "empty";
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Konfiguraation valmius
      </span>
      <span
        className={cn(
          "font-display text-base tracking-tight tabular-nums",
          cardToneText[tone],
        )}
      >
        {coverage} %
      </span>
      <span className="text-[11px] text-muted-foreground">
        {completeness.configured_items}/{completeness.total_items} laitetta
        konfiguroitu · {stageLabel[stage]}
      </span>
    </div>
  );
}

function StatusTab({
  dim,
  active,
  onClick,
}: {
  dim: ConfigDimension;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = TAB_ICON[dim.key];
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        moduleCard,
        "p-4 text-left transition-colors",
        // Active/selected uses `primary` (the app's selection convention) — NOT `accent`, which
        // in the bkry palette is a very light lime that reads too faint as a border/label.
        active
          ? "border-primary bg-primary/10 shadow-[0_0_0_1px_var(--primary)]"
          : "opacity-90 hover:opacity-100",
      )}
    >
      <CardGlowOverlay />
      {/* font-display + tracking-tight matches the app's situation/KPI card values
          (TwinPortfolio, ConfigPortfolio); mono made word-bearing values ("12 aktiivista")
          read oddly. tabular-nums keeps digits aligned across cards — see
          KpiStatusTileContent, the shared inner content this composes. */}
      <KpiStatusTileContent
        icon={Icon}
        label={dim.label}
        value={dim.value}
        tone={dim.tone}
        hint={dim.hint}
        active={active}
      />
    </button>
  );
}
